<script setup>
	import {
		ref,
		computed,
		watch,
		onMounted,
	} from 'vue';
	import QuickLinkItem from './quickLinkItem.vue';
	import QuickLinkContentItem from './quickLinkContentItem.vue';
	import VideoPopup from 'shared/components/VideoElements/videoPopup.vue';
	import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
	import { DsIcon } from '@coloplast/design-system';
	import { Navigation, Pagination, Keyboard, A11y } from "swiper";
	import 'swiper/scss';

	const props = defineProps({
		vm: {
			type: Object,
			required: true
		},
	});

	const settings = {
		speed: 500,
		mousewheel: true,
		keyboard: true,
		watchOverflow: true,
		spaceBetween: 24,
		rewind: true,
		focusableElements: 'article, a',
		breakpoints: {
			1272: {
				slidesPerView: 4,
				slidesPerGroup: 4,
				sliderOffsetAfter: 24,
			},
			991: {
				slidesPerView: 3.2,
				slidesPerGroup: 3,
			},
			599: {
				slidesPerView: 2.2,
				slidesPerGroup: 2,
			},
			0: {
				slidesPerView: 1.2,
				slidesPerGroup: 1,
			},
		},
		navigation: {
			nextEl: '.c-quick-links__arrow--next',
			prevEl: '.c-quick-links__arrow--prev',
		},
		pagination: {
			el: '.c-quick-links__numbers',
			type: 'fraction',
			clickable: false,
		},
		modules: [Navigation, Pagination, Keyboard, A11y],
	};

	const
		videoIsPlaying = ref(false),
		activeTab = ref(null),
		videoUrl = ref(null),
		slidesPerView = ref(0),
		quickLinkWrapper = ref(null)

	const
		showNavigation = computed(() => Math.ceil(currentQuickLinkItems.value / slidesPerView.value) > 1),
		currentQuickLinkItems = computed(() => props.vm.BaseViewModel.QuickLinkItems[activeTab.value].length),
		firstTab = computed(() => props?.vm?.BaseViewModel?.GroupNames ? props.vm.BaseViewModel.GroupNames[0] : null);

	let params;
	if (window.location.search !== '') {
		params = getWindowSearchParams();
	}
	if (params?.ac) {
		//our categories start from UpperCase letter but an old epi transform all search params to lowercase
		let activeParams = params.ac;
		const isFirstLetterUpperCase = (/[A-Z]/.test(activeParams.charAt(0)));
		if (!isFirstLetterUpperCase) {
			activeParams = params.ac.charAt(0).toUpperCase() + params.ac.slice(1);
		}
		if (props?.vm?.BaseViewModel?.GroupNames?.includes(activeParams)) {
			setTagContent(activeParams);
		}
		else {
			activeTab.value = firstTab.value;
		}
	}
	else {
		activeTab.value = firstTab.value;
	}

	onMounted(() => {
		setTimeout(() => {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}, 200);
	});

	watch(() => activeTab.value, (_newVal, oldVal) => {
		if (oldVal) {
			quickLinkWrapper.value.scrollIntoView();
		}
	});

	function getWindowSearchParams() {
		return Object.fromEntries(new URLSearchParams(window.location.search));
	}

	function isQuickLinkItemsShown(tabItem) {
		return props?.vm?.BaseViewModel?.QuickLinkItems[tabItem]?.length > 0;
	}

	function isContentItemsShown(tabItem) {
		return props?.vm?.BaseViewModel?.ContentFeatureItems[tabItem]?.length > 0;
	}

	function setTagContent(tabItem) {
		if (activeTab.value === tabItem) {
			return;
		}
		activeTab.value = tabItem;
		if (tabItem === firstTab.value) {
			updateUrlSearchParams(tabItem, true);
		}
		else {
			updateUrlSearchParams(tabItem);
		}
	}
	function setVideoUrl(video) {
		videoUrl.value = video;
		videoIsPlaying.value = true;
	}
	function initCarousel(swiper) {
		slidesPerView.value = swiper?.params?.slidesPerView;
	}
	function updateUrlSearchParams(tab, deleteParam = false) {
		const
			url = new URL(window.location.href),
			params = getWindowSearchParams();
		if (window.history.pushState) {
			window.history.replaceState(null, null, window.location.pathname);
			if (!deleteParam) {
				if (params?.ac) {
					url.searchParams.set('ac', tab);
				}
				else {
					url.searchParams.append('ac', tab);
				}
			}
			else {
				url.searchParams.delete('ac');
			}
			window.history.pushState({}, '', url.toString());
		}
	}

	function onResize(swiper) {
		slidesPerView.value = swiper?.params?.slidesPerView;
		swiper.slideTo(0);
	}

	function closePopup() {
		videoUrl.value = null;
		videoIsPlaying.value = false;
	}
</script>

<template>
	<div v-if="activeTab"
		 class="c-quick-links"
		 ref="quickLinkWrapper">
		<nav class="c-quick-links__nav c-quick-links__container">
			<span v-if="vm.CurrentBlock.BrowseByLabel"
				  class="c-quick-links__nav-label">
				{{ vm.CurrentBlock.BrowseByLabel }}
			</span>
			<div class="c-quick-links__nav-wrapper">
				<template v-for="tabItem in vm.BaseViewModel.GroupNames">
					<button v-if="isQuickLinkItemsShown(tabItem) || isContentItemsShown(tabItem)"
							:key="tabItem"
							type="button"
							class="c-quick-links__nav-tab"
							:class="{ active: activeTab === tabItem }"
							@click="setTagContent(tabItem)">
						{{ tabItem }}
					</button>
				</template>
			</div>
		</nav>
		<section v-if="isQuickLinkItemsShown(activeTab)"
				 class="c-quick-links__list-container">
			<section class="c-quick-links__list">
				<Swiper v-bind="settings"
						:key="activeTab"
						v-if="vm.BaseViewModel.QuickLinkItems[activeTab].length > 0"
						@init="initCarousel"
						@resize="onResize">
					<SwiperSlide v-for="item in vm.BaseViewModel.QuickLinkItems[activeTab]"
								 :key="item.Headline">
						<QuickLinkItem :item="item" />
					</SwiperSlide>
				</Swiper>
				<div class="c-quick-links__controllers"
					 :class="{'--hidden' : !showNavigation}">
					<button aria-label="Previous"
							class="ds-icon-button ds-icon-button--md ds-icon-button--tertiary c-quick-links__arrow--prev"
							type="button">
						<DsIcon icon-name="chevron-left" size="md"></DsIcon>
					</button>
					<div class="c-quick-links__numbers"></div>
					<button aria-label="Next"
							class="ds-icon-button ds-icon-button--md ds-icon-button--tertiary c-quick-links__arrow--next"
							type="button">
						<DsIcon icon-name="chevron-right" size="md"></DsIcon>
					</button>
				</div>
			</section>
		</section>
		<section v-if="isContentItemsShown(activeTab)"
				 class="c-quick-links__content c-quick-links__container">
			<QuickLinkContentItem v-for="item in vm.BaseViewModel.ContentFeatureItems[activeTab]"
								  :key="item.Headline"
								  :item="item"
								  @clicked="setVideoUrl" />
		</section>
		<VideoPopup v-if="videoIsPlaying && videoUrl"
					@close-popup="closePopup"
					:video-url="videoUrl" />
	</div>
</template>
